import React, { useContext, useEffect, useRef, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { AppContext } from '../../../contexts/AppContext';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { CenterDataForm, defaultCenterDataForm } from '../../../app/models/Center';
import { Country } from '../../../app/models/Country';
import { Region } from '../../../app/models/Region';
import { Commune } from '../../../app/models/Commune';
import useCenterService from '../../../app/services/hooks/useCenterService';
import useCommuneService from '../../../app/services/hooks/useCommuneService';
import useCountryService from '../../../app/services/hooks/useCountryService';
import CenterFormContainer from './CenterFormContainer';

interface Props {
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const CenterCreate = ({ onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);

    const [errorGetData, setErrorGetData] = useState('');
    const [center, setCenter] = useState<CenterDataForm>(defaultCenterDataForm);
    const [countries, setCountries] = useState<Country[]>([]);
    const [regions, setRegions] = useState<Region[]>([]);
    const regionIdSelected = useRef<number | undefined>(undefined);
    const countryIdSelected = useRef<number | undefined>(undefined);
    const [communes, setCommunes] = useState<Commune[]>([]);
    const [errorFields, setErrorFields] = useState<any>();
    const { fetchingCreateCenter, createCenter, fetchingStoreCenter, storeCenter } =
        useCenterService();
    const { getCommunesByRegion, fetchingGetCommunesByRegion } = useCommuneService();
    const { getRegionsByCountry, fetchingGetRegionsByCountry } = useCountryService();

    useEffect(() => {
        create();
    }, []);

    useEffect(() => {
        if (center.region_id == undefined) {
            regionIdSelected.current = undefined;
        }
        if (center.region_id && center.region_id != regionIdSelected.current) {
            getCommunesByRegion(center.region_id, {
                onSuccess: (response: ServiceResponse) => {
                    regionIdSelected.current = center.region_id ?? 0;

                    setCommunes(response.data.communes);
                    setCenter({
                        ...center,
                        commune_id: undefined
                    });
                    setErrorFields({
                        ...errorFields,
                        region_id: '',
                        commune_id: ''
                    });
                },
                onError: (response: ServiceResponse) => {
                    toast.error(response.message);
                    setCenter({
                        ...center,

                        region_id: regionIdSelected.current
                    });
                }
            });
        }
    }, [center.region_id]);

    useEffect(() => {
        if (center.region_id == undefined) {
            countryIdSelected.current = undefined;
        }
        if (center.country_id && center.country_id != countryIdSelected.current) {
            getRegionsByCountry(center.country_id, {
                onSuccess: (response: ServiceResponse) => {
                    countryIdSelected.current = center.country_id ?? 0;

                    setRegions(response.data.regions);
                    setCenter({
                        ...center,
                        region_id: undefined
                    });
                    setErrorFields({
                        ...errorFields,
                        region_id: '',
                        country_id: ''
                    });
                },
                onError: (response: ServiceResponse) => {
                    toast.error(response.message);
                    setCenter({
                        ...center,
                        country_id: countryIdSelected.current
                    });
                }
            });
        }
    }, [center.country_id]);

    const create = () => {
        if (showLoading) showLoading('loading', 'Cargando datos...');
        createCenter({
            onSuccess: (response: ServiceResponse) => {
                setCountries(response.data.countries);
                if (hideLoading) hideLoading();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                if (onError) onError();
            }
        });
    };

    const store = () => {
        if (showLoading) showLoading('loading', 'Guardando centro...');
        storeCenter(center, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });
                toast.success(response.message, {
                    autoClose: 2500
                });
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();

                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    if (errorGetData !== '') {
        return <div>{errorGetData}</div>;
    }

    return (
        <CenterFormContainer
            action={store}
            cancel={cancel}
            center={center}
            setCenter={setCenter}
            errorFields={errorFields}
            countries={countries}
            regions={regions}
            communes={communes}
            setCommunes={setCommunes}
            setRegions={setRegions}
            fetching={fetchingStoreCenter}
            fetchingCommunes={fetchingGetCommunesByRegion}
            fetchingRegions={fetchingGetRegionsByCountry}
        />
    );
};

export default CenterCreate;
